import { cloneDeep, get, set } from 'lodash';
import { useCallback, useMemo } from 'react';
import { Machine } from '../../api/graphql/generated';
import {
  UserPreferences,
  useGetUserPreference,
  useSetUserPreferenceLazy,
} from '../../api/hooks/preference.hooks';
import { trackFavoriteIO, trackFavoriteVariable } from '../../config/analytics';

export interface FavoritesInput {
  path: string;
  favorite: string;
}

export interface MachineFavoritesInput {
  machine: Machine;
  favorite: string;
}

interface FavoritesHelpers {
  addMachineFavoriteVariable: (
    input: MachineFavoritesInput
  ) => Promise<boolean>;
  removeMachineFavoriteVariable: (
    input: MachineFavoritesInput
  ) => Promise<boolean>;
  addMachineFavoriteIO: (input: MachineFavoritesInput) => Promise<boolean>;
  removeMachineFavoriteIO: (input: MachineFavoritesInput) => Promise<boolean>;
}

type Favorites = string[];
type MachineFavoriteIo = Record<string, Favorites>;
type MachineFavoriteVariables = Record<string, Record<string, Favorites>>;
type MachineFavoriteType =
  | UserPreferences.FAVORITE_IO
  | UserPreferences.FAVORITE_VARIABLES;

export const useFavoritesHelpers = (): FavoritesHelpers => {
  const [setUserPreference] = useSetUserPreferenceLazy<
    MachineFavoriteIo | MachineFavoriteVariables
  >();
  const favoriteIoPreference = useGetUserPreference<MachineFavoriteIo>(
    UserPreferences.FAVORITE_IO
  );
  const favoriteVariablePreference =
    useGetUserPreference<MachineFavoriteVariables>(
      UserPreferences.FAVORITE_VARIABLES
    );

  const favoriteIo: MachineFavoriteIo | undefined = favoriteIoPreference?.value;
  const favoriteVariables: MachineFavoriteVariables | undefined =
    favoriteVariablePreference?.value;

  const addMachineFavoritePreference = useCallback(
    (
      favoriteType: MachineFavoriteType,
      path: string,
      favorite: string,
      existingFavorites:
        | MachineFavoriteIo
        | MachineFavoriteVariables
        | undefined
    ) => {
      let value: MachineFavoriteIo | MachineFavoriteVariables = {};

      if (existingFavorites === undefined) {
        value = set({}, path, [favorite]);
      } else {
        const currentFavorite = get(existingFavorites, path);
        if (currentFavorite === undefined) {
          value = set({ ...existingFavorites }, path, [favorite]);
        } else {
          if (Array.isArray(currentFavorite)) {
            value = set(cloneDeep(existingFavorites), path, [
              ...currentFavorite,
              favorite,
            ]);
          }
        }
      }

      setUserPreference({ key: favoriteType, value });
    },
    [setUserPreference]
  );

  const addMachineFavoriteVariablePreference = useCallback(
    (input: MachineFavoritesInput, path: string) => {
      addMachineFavoritePreference(
        UserPreferences.FAVORITE_VARIABLES,
        path,
        input.favorite,
        favoriteVariables
      );
    },
    [addMachineFavoritePreference, favoriteVariables]
  );

  const addMachineFavoriteIoPreference = useCallback(
    (input: MachineFavoritesInput, path: string) => {
      addMachineFavoritePreference(
        UserPreferences.FAVORITE_IO,
        path,
        input.favorite,
        favoriteIo
      );
    },
    [addMachineFavoritePreference, favoriteIo]
  );

  const removeMachineFavorite = useCallback(
    (
      favoriteType: MachineFavoriteType,
      path: string | string[],
      favorite: string,
      existingFavorites:
        | MachineFavoriteIo
        | MachineFavoriteVariables
        | undefined
    ) => {
      const currentFavorite = get(existingFavorites, path);

      if (
        existingFavorites === undefined ||
        currentFavorite === undefined ||
        !Array.isArray(currentFavorite)
      ) {
        return;
      }

      const value = set(
        cloneDeep(existingFavorites),
        path,
        currentFavorite.filter((v) => v !== favorite)
      );
      setUserPreference({ key: favoriteType, value });
    },
    [setUserPreference]
  );

  const removeMachineFavoriteVariable = useCallback(
    (input: MachineFavoritesInput, path: string) => {
      removeMachineFavorite(
        UserPreferences.FAVORITE_VARIABLES,
        path,
        input.favorite,
        favoriteVariables
      );
    },
    [favoriteVariables, removeMachineFavorite]
  );

  const removeMachineFavoriteIo = useCallback(
    (input: MachineFavoritesInput, path: string) => {
      removeMachineFavorite(
        UserPreferences.FAVORITE_IO,
        path,
        input.favorite,
        favoriteIo
      );
    },
    [favoriteIo, removeMachineFavorite]
  );

  return useMemo(
    () => ({
      addMachineFavoriteVariable: async (
        input: MachineFavoritesInput
      ): Promise<boolean> => {
        const path = getPathForMachineVariables(input.machine);
        if (path) {
          trackFavoriteVariable();
          addMachineFavoriteVariablePreference(input, path);
          return true;
        } else {
          return false;
        }
      },
      removeMachineFavoriteVariable: async (
        input: MachineFavoritesInput
      ): Promise<boolean> => {
        const path = getPathForMachineVariables(input.machine);
        if (path) {
          removeMachineFavoriteVariable(input, path);
          return true;
        } else {
          return false;
        }
      },
      addMachineFavoriteIO: async (
        input: MachineFavoritesInput
      ): Promise<boolean> => {
        const path = getPathForMachineIO(input.machine);
        if (path) {
          trackFavoriteIO();
          addMachineFavoriteIoPreference(input, path);
          return true;
        } else {
          return false;
        }
      },
      removeMachineFavoriteIO: async (
        input: MachineFavoritesInput
      ): Promise<boolean> => {
        const path = getPathForMachineIO(input.machine);
        if (path) {
          removeMachineFavoriteIo(input, path);
          return true;
        } else {
          return false;
        }
      },
    }),
    [
      addMachineFavoriteIoPreference,
      addMachineFavoriteVariablePreference,
      removeMachineFavoriteVariable,
      removeMachineFavoriteIo,
    ]
  );
};

export const getPathForMachineVariables = (
  machine: Machine
): string | undefined => {
  const programName = machine.state?.runtimeInfo?.programName;
  if (programName) {
    return `${machine.id}.${programName.replace(/\./g, '!')}`;
  } else {
    return `${machine.id}.__unknownprogram__!`;
  }
};

const getPathForMachineIO = (machine: Machine): string | undefined => {
  return `${machine.id}`;
};

export const useMachineVariableFavorites = (
  machine?: Machine
): string[] | undefined => {
  const favoriteVariablePreference =
    useGetUserPreference<MachineFavoriteVariables>(
      UserPreferences.FAVORITE_VARIABLES
    );
  if (machine) {
    const path = getPathForMachineVariables(machine);
    if (path) {
      const vars = get(favoriteVariablePreference?.value ?? {}, path);
      if (Array.isArray(vars)) {
        return vars;
      }
    }
  }
};

export const useMachineIOFavorites = (
  machine?: Machine
): string[] | undefined => {
  const favoriteIoPreference = useGetUserPreference<MachineFavoriteIo>(
    UserPreferences.FAVORITE_IO
  );
  if (machine) {
    const path = getPathForMachineIO(machine);
    if (path) {
      return get(favoriteIoPreference?.value ?? {}, path);
    }
  }
};
